/**
 * Toast: globaler nicht-blockierender Status-Stack (ersetzt alert()).
 * - useToast() liefert push(message, kind?, durationMs?).
 * - Convenience-Methoden akzeptieren optional Detailtext ODER Dauer.
 * - <Toaster /> wird einmalig in App.tsx gemountet.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, Info, X, AlertCircle } from 'lucide-react';

export type ToastKind = 'success' | 'error' | 'warn' | 'info';
type ToastExtra = string | number | undefined;

interface ToastItem {
  id: number;
  message: string;
  desc?: string;
  kind: ToastKind;
  duration: number;
}

interface ToastApi {
  push: (message: string, kind?: ToastKind, durationMs?: number) => void;
  success: (message: string, extra?: ToastExtra, durationMs?: number) => void;
  error: (message: string, extra?: ToastExtra, durationMs?: number) => void;
  warn: (message: string, extra?: ToastExtra, durationMs?: number) => void;
  info: (message: string, extra?: ToastExtra, durationMs?: number) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

const KIND_STYLE: Record<ToastKind, { border: string; text: string; bg: string; icon: typeof CheckCircle2 }> = {
  success: { border: 'border-ok/40',     text: 'text-ok',     bg: 'bg-ok/10',     icon: CheckCircle2 },
  error:   { border: 'border-danger/40', text: 'text-danger', bg: 'bg-danger/10', icon: AlertCircle },
  warn:    { border: 'border-warn/40',   text: 'text-warn',   bg: 'bg-warn/10',   icon: AlertTriangle },
  info:    { border: 'border-accent/40', text: 'text-accent', bg: 'bg-accent/10', icon: Info },
};

function extraDesc(extra?: ToastExtra): string | undefined {
  return typeof extra === 'string' && extra.trim() ? extra : undefined;
}
function extraDuration(extra?: ToastExtra, durationMs?: number): number | undefined {
  return typeof extra === 'number' ? extra : durationMs;
}

export function Toaster({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const push = useCallback((message: string, kind: ToastKind = 'info', durationMs = 4500, desc?: string): void => {
    // Dedup: eine identische Meldung (kind+message+desc), die erneut gepusht
    // wird waehrend sie noch sichtbar ist, stapelt sich nicht erneut.
    // Verhindert Toast-Fluten bei Polling-Storms (z.B. wiederholte 429/5xx-Fehler).
    setItems(arr => {
      if (arr.some(t => t.kind === kind && t.message === message && (t.desc ?? '') === (desc ?? ''))) return arr;
      const id = ++idRef.current;
      return [...arr, { id, message, desc, kind, duration: durationMs }];
    });
  }, []);

  const dismiss = useCallback((id: number): void => {
    setItems(arr => arr.filter(t => t.id !== id));
  }, []);

  const api: ToastApi = {
    push,
    success: (m, e, d) => push(m, 'success', extraDuration(e, d), extraDesc(e)),
    error:   (m, e, d) => push(m, 'error', extraDuration(e, d), extraDesc(e)),
    warn:    (m, e, d) => push(m, 'warn', extraDuration(e, d), extraDesc(e)),
    info:    (m, e, d) => push(m, 'info', extraDuration(e, d), extraDesc(e)),
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="fixed top-4 right-4 z-[1000] flex flex-col gap-2 pointer-events-none max-w-sm w-[calc(100%-2rem)]" aria-live="polite" aria-atomic="false">
        {items.map(t => <ToastRow key={t.id} item={t} onDismiss={() => dismiss(t.id)} />)}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastRow({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const style = KIND_STYLE[item.kind];
  const Icon = style.icon;
  useEffect(() => {
    if (item.duration <= 0) return;
    const t = setTimeout(onDismiss, item.duration);
    return () => clearTimeout(t);
  }, [item.duration, onDismiss]);
  return (
    <div role="status" className={`pointer-events-auto rounded-md border ${style.border} ${style.bg} shadow-lg backdrop-blur-sm px-3 py-2 flex items-start gap-2 transition-all`} style={{ animation: 'toast-in 180ms ease-out' }}>
      <Icon className={`h-4 w-4 ${style.text} mt-0.5 shrink-0`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${style.text} break-words`}>{item.message}</p>
        {item.desc && <p className="text-xs text-muted break-words mt-0.5">{item.desc}</p>}
      </div>
      <button type="button" onClick={onDismiss} className={`${style.text} opacity-60 hover:opacity-100 shrink-0`} aria-label="Benachrichtigung schliessen"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast() muss innerhalb von <Toaster> verwendet werden.');
  return ctx;
}
